import { supabase } from '../lib/supabase';

/**
 * Payment Service - Handles Cashfree payment integration
 */
export class PaymentService {
  /**
   * Create payment order via backend Edge Function
   * @param {string} userId - User ID
   * @param {string} planId - Plan ID (e.g., 'premium')
   * @param {number} amount - Amount in INR
   * @param {string} customerEmail - Customer email
   * @param {string} customerName - Customer name
   * @param {string} customerPhone - Customer phone
   * @returns {Promise<Object>} Payment order data with payment_session_id
   */
  static async createOrder(userId, planId, amount, customerEmail, customerName, customerPhone) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User not authenticated');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/create-payment-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          planId,
          amount,
          customerEmail,
          customerName,
          customerPhone
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create payment order');
      }

      const orderData = await response.json();
      return orderData;
    } catch (error) {
      console.error('Create order error:', error);
      throw new Error(error.message || 'Failed to create payment order. Please try again.');
    }
  }

  /**
   * Verify payment status via backend Edge Function
   * @param {string} orderId - Order ID from Cashfree
   * @returns {Promise<Object>} Payment verification result
   */
  static async verifyPayment(orderId) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User not authenticated');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/verify-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orderId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to verify payment');
      }

      const verificationData = await response.json();
      return verificationData;
    } catch (error) {
      console.error('Payment verification error:', error);
      throw new Error(error.message || 'Failed to verify payment.');
    }
  }

  /**
   * Update user tier after successful payment
   * @param {string} userId - User ID
   * @param {string} tier - New tier ('premium' or 'enterprise')
   * @returns {Promise<Object>} Updated user profile
   */
  static async updateUserTier(userId, tier) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ tier })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Update tier error:', error);
      throw new Error('Failed to update account. Please contact support.');
    }
  }
}

export default PaymentService;